import { useState } from "react";

import { PatientDocumentValidationStatus } from "@/constants/Messages";
import type { GroupChannel } from "@/crossPlatform/Sendbird";
import useWebSafeChatPatientId from "@/hooks/useWebSafeChatPatientId";
import useAppMonitor from "@/services/AppMonitor";
import { useLazyGetPatientDocumentDownloadURLQuery } from "@/store/api/chat";
import { useAppSelector } from "@/store/hooks";
import { useAppUserSelector } from "@/store/reducers/appUser";
import {
  selectInvalidAttachments,
  useAttachmentsDispatch,
} from "@/store/reducers/attachments";
import AttachmentPreviewModal from "@/ui/components/messages/documents/AttachmentPreviewModal";
import BaseDocumentCard from "@/ui/core/BaseDocumentCard";
import { getDocumentSentDateDescription } from "@/utils/documents.util";

interface AttachmentDocumentCardProps extends ViewWithTestID {
  channel: GroupChannel;
  document: PatientDocument;
}

const AttachmentDocumentCard = ({
  channel,
  document,
}: AttachmentDocumentCardProps) => {
  const { externalId: appUserID } = useAppUserSelector();
  const selectedPatientId = useWebSafeChatPatientId(channel);
  const { captureMessage } = useAppMonitor();
  const [isDetailModalOpen, setIsDetailModalOpen] = useState<boolean>(false);

  const invalidAttachments = useAppSelector(selectInvalidAttachments);
  const { addInvalidAttachment } = useAttachmentsDispatch();

  const sentDateDescription = getDocumentSentDateDescription({
    author: document.createdBy,
    dateString: document.createdAt,
    isSentByAppUser: document.createdBy.externalId === appUserID,
  });

  const compoundFileName = `${document.documentExternalId}-${document.fileName}`;

  const [query, { data, isLoading }] =
    useLazyGetPatientDocumentDownloadURLQuery();

  const handleDocumentPress = () => {
    if (!selectedPatientId) {
      captureMessage("selectedPatientId is missing from chat-channel-context");
      return;
    }

    void query({
      fileName: compoundFileName,
      patientExternalId: selectedPatientId,
    })
      .unwrap()
      .then((response) => {
        if (
          response.fileValidationStatus ===
          PatientDocumentValidationStatus.Invalid
        ) {
          addInvalidAttachment(document.documentExternalId);
          return;
        }

        setIsDetailModalOpen(true);
      });
  };

  const handleDetailModalClose = () => {
    setIsDetailModalOpen(false);
  };

  const documentURI = data?.attachmentDownloadUrl;
  const isInvalid = invalidAttachments.includes(document.documentExternalId);

  return (
    <>
      <BaseDocumentCard
        description={sentDateDescription}
        fileName={document.fileName}
        id={document.documentExternalId}
        isInvalid={isInvalid}
        isLoading={isLoading}
        onPress={handleDocumentPress}
        testID={document.documentExternalId}
      />
      {documentURI && (
        <AttachmentPreviewModal
          isOpen={isDetailModalOpen}
          onClose={handleDetailModalClose}
          title={document.fileName}
          uri={documentURI}
        />
      )}
    </>
  );
};

export default AttachmentDocumentCard;
